import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Pencil, Trash2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SafeHtml } from "@/components/common/SafeHtml";
import { UserAvatar } from "@/components/common/UserAvatar";
import { InvoiceAssigneeSelect } from "@/components/invoices/InvoiceAssigneeSelect";
import { InvoiceLabels } from "@/components/invoices/InvoiceLabels";
import { selectCurrentUser } from "@/features/auth/authSlice";
import { isApprovalRequired } from "@/lib/invoiceDetail";
import { formatDateTime } from "@/lib/invoiceStatus";
import { hasRichTextContent } from "@/lib/richText";

/**
 * Renders nothing unless there is a real alert to show. The mapper has already
 * dropped labels with no text and duplicate references with no guid, so an
 * empty `labels` array here means there is genuinely nothing to say.
 */
export function InvoiceDetailAlerts({ detail }) {
  const duplicate = detail.duplicateOfInvoice;
  const labels = detail.labels ?? [];

  if (!duplicate && labels.length === 0) return null;

  return (
    <div className="invoice-detail-alerts">
      {duplicate && (
        <div className="invoice-detail-alert">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          <p className="text-sm">
            Possible duplicate of{" "}
            <Link to={`/invoice/view/${duplicate.guid}`} className="font-semibold underline">
              {duplicate.invoiceNumber ?? "another invoice"}
            </Link>
            . Review before approving.
          </p>
        </div>
      )}

      {/* No `maxVisible`: the panel has room, and on the record itself every
          label is worth reading rather than hidden behind a +N chip. */}
      <InvoiceLabels labels={labels} />
    </div>
  );
}

export function InvoiceDetailAssignment({
  detail,
  viewOnly,
  assignees,
  onRequestAssignees,
  onAssigneeChange,
  clientName,
  clientNameError,
  onClientNameChange,
  onClientNameSave,
  isSavingClientName,
  isApproved,
  approvalError,
  approvalAttempt,
  onApprovedChange,
}) {
  const approvalRef = useRef(null);
  // The choice cannot be made on the view-only route, so nothing is asked of the
  // reader there: no hint, no required flag, no error.
  const approvalRequired = isApprovalRequired(detail) && !viewOnly;
  // An error from an earlier attempt is dropped once the choice stops being
  // required, for instance when the status is changed from the header.
  const shownApprovalError = approvalRequired ? approvalError : null;

  // After a save is blocked, bring the control into view and focus it so the
  // error is not left off-screen below the fold.
  useEffect(() => {
    if (!shownApprovalError || !approvalRef.current) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    approvalRef.current.scrollIntoView({
      block: "center",
      behavior: reduceMotion ? "auto" : "smooth",
    });
    approvalRef.current.querySelector("button:not(:disabled)")?.focus({ preventScroll: true });
  }, [approvalAttempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const clientNameChanged = clientName !== (detail.clientName ?? "");

  return (
    <div className="invoice-detail-meta">
      <div className="invoice-detail-meta-block">
        <p className="invoice-detail-label">Assigned to</p>
        {/* Handing an invoice on is not an edit of its data, so it stays available
            when the form is locked or read-only — the list allows it too. */}
        <InvoiceAssigneeSelect
          invoice={detail}
          assignees={assignees}
          onChange={onAssigneeChange}
          onRequestAssignees={onRequestAssignees}
        />
      </div>

      <div className="invoice-detail-meta-block">
        <label htmlFor="client-name" className="invoice-detail-label">
          Client name
        </label>
        <div className="flex items-start gap-2">
          <Input
            id="client-name"
            value={clientName}
            disabled={viewOnly}
            aria-invalid={Boolean(clientNameError)}
            onChange={(event) => onClientNameChange(event.target.value)}
            className="h-9 rounded-[0.8rem] border-line bg-background text-sm shadow-none"
          />
          {!viewOnly && (
            <Button
              type="button"
              variant="default"
              className="h-9 shrink-0 rounded-[0.8rem] px-4 shadow-none"
              disabled={!clientNameChanged || Boolean(clientNameError) || isSavingClientName}
              onClick={onClientNameSave}
            >
              {isSavingClientName ? <Loader2 className="size-4 animate-spin" /> : "Save"}
            </Button>
          )}
        </div>
        {clientNameError ? (
          <p className="invoice-detail-error" role="alert">
            {clientNameError}
          </p>
        ) : null}
      </div>

      {detail.isSampleInvoice && (
        <div className="invoice-detail-meta-block">
          <p id="approval-label" className="invoice-detail-label">
            Approval
            {approvalRequired && <span className="invoice-detail-required"> · Required</span>}
          </p>
          <div
            role="radiogroup"
            aria-labelledby="approval-label"
            aria-required={approvalRequired}
            aria-invalid={Boolean(shownApprovalError)}
            data-invalid={Boolean(shownApprovalError)}
            ref={approvalRef}
            className="invoice-approval"
          >
            {[
              { value: true, label: "Approved", tone: "success", Icon: CheckCircle2 },
              { value: false, label: "Not approved", tone: "danger", Icon: XCircle },
            ].map(({ value, label, tone, Icon }) => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={isApproved === value}
                data-tone={tone}
                disabled={viewOnly}
                className="invoice-approval-option"
                onClick={() => onApprovedChange(value)}
              >
                <Icon className="size-4" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          {shownApprovalError ? (
            <p className="invoice-detail-error" role="alert">
              {shownApprovalError}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

const CommentEditor = lazy(() => import("@/components/invoices/detail/CommentEditor"));

function CommentEditorFallback() {
  return (
    <div
      role="status"
      className="flex h-64 items-center justify-center gap-2 rounded-[0.8rem] border border-line bg-background text-sm text-muted-foreground"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      Loading editor…
    </div>
  );
}

/**
 * Comments come from the API as rich-text HTML, so they are sanitised on the way
 * in. Only the author may edit or delete their own comment — the same rule the
 * old app applied.
 *
 * Deliberately *not* gated on the form's read-only state. Commenting is a
 * discussion action, not a mutation of the invoice, and the old app treated it
 * that way: its comments card sat outside every `viewOnly` guard in
 * `EditInvoice.jsx` and was conditioned only on the query having loaded. Reusing
 * `viewOnly` here took three things away that used to work — commenting from the
 * view route (where every queue but Approval Requests sends you), commenting
 * without `UPDATE_INVOICE`, and annotating an invoice after it was approved or
 * processed, which is when a note is most worth leaving. There is no comment
 * permission in the auth service to gate on either.
 */
export function InvoiceDetailCommentsPanel({
  comments = [],
  isLoading,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  isSubmitting,
}) {
  const currentUser = useSelector(selectCurrentUser);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null);

  // If the comment being edited disappears (deleted elsewhere), fall back to
  // composing a new one. Derived rather than corrected in an effect, so there is
  // no frame where the form still points at a comment that is gone.
  const editingComment =
    editing && comments.some((comment) => comment.guid === editing.guid) ? editing : null;

  const submit = async (body) => {
    if (!hasRichTextContent(body)) return;

    if (editingComment) {
      await onUpdateComment(editingComment.guid, body);
    } else {
      await onAddComment(body);
    }
    setEditing(null);
    setDraft("");
  };

  const startEdit = (comment) => {
    setEditing(comment);
    // Comments are stored as HTML and edited as HTML — the editor round-trips
    // the markup, so formatting and attachment links survive an edit. The plain
    // textarea this replaced flattened both away.
    setDraft(comment.description ?? "");
  };

  if (isLoading) {
    return (
      <div role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading comments…
      </div>
    );
  }

  return (
    <div className="invoice-detail-comments">
      <ul className="space-y-3">
        {comments.map((comment) => {
          const isAuthor = currentUser?.guid && currentUser.guid === comment.createdBy?.guid;

          return (
            <li key={comment.guid} className="invoice-detail-comment">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">
                  {comment.createdBy?.name ?? "Unknown"}
                </p>
                <div className="flex items-center gap-1">
                  <time className="text-xs text-muted-foreground" dateTime={comment.createdOn}>
                    {formatDateTime(comment.createdOn)}
                  </time>
                  {isAuthor && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Edit comment"
                        onClick={() => startEdit(comment)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Delete comment"
                        onClick={() => onDeleteComment(comment.guid)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <SafeHtml
                html={comment.description}
                className="invoice-detail-comment-body mt-1 text-sm leading-relaxed text-muted-foreground"
              />
            </li>
          );
        })}
        {comments.length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
      </ul>

      <div className="mt-4 space-y-2">
        <p className="invoice-detail-label">
          {editingComment ? "Edit comment" : "Add comment"}
        </p>
        {/* The editor pulls in the Syncfusion rich-text bundle, so it is
            loaded on demand rather than with the detail route. */}
        <Suspense fallback={<CommentEditorFallback />}>
          <CommentEditor
            value={draft}
            onChange={setDraft}
            onSubmit={submit}
            onCancel={() => {
              setEditing(null);
              setDraft("");
            }}
            isSubmitting={isSubmitting}
            isEditing={Boolean(editingComment)}
          />
        </Suspense>
      </div>
    </div>
  );
}

export function InvoiceDetailActivityPanel({ activity = [], isLoading }) {
  if (isLoading) {
    return (
      <div role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading activity…
      </div>
    );
  }

  if (activity.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No activity recorded yet.</p>;
  }

  // An unparseable `createdOn` (missing/malformed) times as 0 rather than
  // `NaN` — the comparator has to return a number, and `NaN` sorts an entry
  // into a browser-dependent spot instead of consistently to the end.
  const timeOf = (item) => {
    const time = new Date(item.createdOn).getTime();
    return Number.isFinite(time) ? time : 0;
  };
  const sortedActivity = [...activity].sort((a, b) => timeOf(b) - timeOf(a));

  return (
    <ul className="invoice-detail-activity">
      {sortedActivity.map((item) => (
        <li key={item.id}>
          <UserAvatar user={item.actionedBy} guid={item.actionedBy?.guid} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                {item.actionedBy?.name ?? "System"}
              </p>
              <time className="invoice-detail-activity-time text-xs" dateTime={item.createdOn}>
                {formatDateTime(item.createdOn)}
              </time>
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">{item.message}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
